import { WbAutocomplete, WbTextField } from '@agilelab/plugin-wb-platform';
import { stringifyEntityRef, Entity } from '@mainstage/catalog-model';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SYSTEM_PROTOTYPES_QUERY_KEY } from '../hooks/constants';
import {
  useApi,
  alertApiRef,
  identityApiRef,
} from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import {
  Box,
  Typography,
  FormHelperText,
  CircularProgress,
  Grid,
} from '@material-ui/core';
import {
  PrototypeStatus,
  PrototypeStatusBadge,
  systemPrototypeApiRef,
  SystemPrototype,
} from '@agilelab/plugin-wb-system-prototype-common';

export interface DataProductRequestFormProps {
  taxonomyRef: string;
  onSave: (data: {
    name: string;
    domain: string;
    owner: string;
    systemPrototypeType: string;
    description: string;
    purpose: string;
    status: string;
  }) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

interface FormValues {
  name: string;
  domain: string;
  owner: string;
  systemPrototypeType: string;
  description: string;
  purpose: string;
  status: string;
}

interface DomainOption {
  value: string;
  label: string;
}

interface TypeOption {
  value: string;
  label: string;
}

function StatusSelector({
  value,
  onChange,
  error,
  helperText,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}) {
  return (
    <Box>
      <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
        Status *
      </Typography>
      <Box
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginTop: 8,
          marginBottom: 8,
        }}
      >
        {Object.values(PrototypeStatus).map(statusKey => (
          <PrototypeStatusBadge
            status={statusKey}
            key={statusKey}
            aria-selected={value === statusKey}
            style={{
              fontWeight: value === statusKey ? 700 : 400,
              cursor: disabled ? 'not-allowed' : 'pointer',
              marginRight: 8,
              marginBottom: 8,
              border: value === statusKey ? '2px solid #1976d2' : 'none',
            }}
            onClick={() => !disabled && onChange(statusKey)}
          />
        ))}
      </Box>
      {error && (
        <FormHelperText error style={{ width: '100%' }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
}

const DataProductRequestForm: FC<DataProductRequestFormProps> = ({
  taxonomyRef,
  onSave,
  onSubmittingChange,
}) => {
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const identityApi = useApi(identityApiRef);
  const systemPrototypeApi = useApi(systemPrototypeApiRef);
  const [domains, setDomains] = useState<DomainOption[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [loadingOwners, setLoadingOwners] = useState(true);
  const [loadingTypes, setLoadingTypes] = useState(true);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!taxonomyRef) {
      setLoadingDomains(false);
      return;
    }
    catalogApi
      .getEntities({ filter: { kind: 'Domain' } })
      .then((resp: { items: Entity[] }) =>
        setDomains(
          resp.items.map(i => ({
            value: `domain:default/${i.metadata.name}`,
            label: (i.spec?.mesh as any)?.name || i.metadata.name,
          })),
        ),
      )
      .finally(() => setLoadingDomains(false));
  }, [catalogApi, taxonomyRef]);

  useEffect(() => {
    catalogApi
      .getEntities({ filter: { kind: 'User' } })
      .then(resp => setOwners(resp.items.map(i => i.metadata.name)))
      .finally(() => setLoadingOwners(false));
  }, [catalogApi]);

  useEffect(() => {
    if (!taxonomyRef) {
      setLoadingTypes(false);
      return;
    }

    const fetchTypes = async () => {
      try {
        const [typesResponse, templatesResponse, systemTypesResponse] =
          await Promise.all([
            catalogApi.getEntities({ filter: { kind: 'SystemPrototypeType' } }),
            catalogApi.getEntities({
              filter: { kind: 'SystemPrototypeTemplate' },
            }),
            taxonomyRef === 'all'
              ? Promise.resolve({ items: [] })
              : catalogApi.getEntities({
                  filter: {
                    kind: 'SystemType',
                    'spec.belongsTo': taxonomyRef,
                  },
                }),
          ]);

        const templateRefs = new Set(
          templatesResponse.items.map(template =>
            stringifyEntityRef({
              kind: 'SystemPrototypeTemplate',
              namespace: 'default',
              name: template.metadata.name,
            }),
          ),
        );

        const systemTypeRefs = new Set(
          systemTypesResponse.items.map(type =>
            stringifyEntityRef({
              kind: 'SystemType',
              namespace: 'default',
              name: type.metadata.name,
            }),
          ),
        );

        // Single pass filtering with early returns
        const filteredTypes = typesResponse.items
          .filter(type => {
            const typeTemplate = type.spec?.template?.toString();
            if (!typeTemplate || !templateRefs.has(typeTemplate)) {
              return false;
            }

            if (taxonomyRef === 'all') {
              return true;
            }

            const evolvesInto = type.spec?.evolvesInto?.toString();
            return evolvesInto && systemTypeRefs.has(evolvesInto);
          })
          .map(type => ({
            value: type.metadata.name,
            label: String(type.metadata.displayName || type.metadata.name),
          }));

        setTypes(filteredTypes);
      } catch (error) {
        alertApi.post({
          message: 'Error fetching system prototype types',
          severity: 'error',
        });
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchTypes();
  }, [catalogApi, taxonomyRef, alertApi]);

  const loading = loadingDomains || loadingOwners || loadingTypes;

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      domain: '',
      owner: '',
      systemPrototypeType: '',
      description: '',
      purpose: '',
      status: '',
    },
  });

  const submitHandler = async (data: FormValues) => {
    onSubmittingChange?.(true);
    try {
      const { token } = await identityApi.getCredentials();
      await systemPrototypeApi.createSystemPrototype(
        data as unknown as SystemPrototype,
        { token },
      );
      alertApi.post({
        message: 'System Prototype created successfully',
        severity: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: [SYSTEM_PROTOTYPES_QUERY_KEY],
      });
      onSave(data);
    } catch (error) {
      if (error instanceof Error && error.message === 'DUPLICATE_NAME') {
        setError('name', {
          type: 'manual',
          message: 'A system prototype with this name already exists',
        });
      } else {
        alertApi.post({
          message: 'Error submitting data product request',
          severity: 'error',
        });
      }
    } finally {
      onSubmittingChange?.(false);
    }
  };

  if (loading) {
    return (
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        p={2}
      >
        <CircularProgress />
        <Typography variant="body1" style={{ marginTop: 16 }}>
          Loading domains, owners & types…
        </Typography>
      </Box>
    );
  }

  return (
    <Box width="100%" display="flex" flexDirection="column" height="100%">
      <form
        id="new-prototype-form"
        onSubmit={handleSubmit(submitHandler)}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid container spacing={2} style={{ width: '100%' }}>
          <Grid item xs={12}>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <>
                  <WbTextField
                    {...field}
                    label="Name *"
                    placeholder="Enter a unique and descriptive name"
                    fullWidth
                    error={!!errors.name}
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <FormHelperText error>{errors.name.message}</FormHelperText>
                  )}
                </>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="owner"
              control={control}
              rules={{ required: 'Owner is required' }}
              render={({ field }) => (
                <WbAutocomplete
                  options={owners}
                  value={owners.includes(field.value) ? field.value : null}
                  onChange={(_, v) => field.onChange(v ?? '')}
                  getOptionLabel={opt => opt}
                  label="Owner *"
                  placeholder="Select an owner"
                  fullWidth
                  error={!!errors.owner}
                  helperText={errors.owner?.message ?? ''}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="systemPrototypeType"
              control={control}
              rules={{ required: 'Type is required' }}
              render={({ field }) => (
                <WbAutocomplete
                  options={types}
                  value={types.find(t => t.value === field.value) || null}
                  onChange={(_, v) => field.onChange(v?.value ?? '')}
                  getOptionLabel={opt => opt?.label || ''}
                  label="Type *"
                  placeholder="Select a type"
                  fullWidth
                  error={!!errors.systemPrototypeType}
                  helperText={
                    errors.systemPrototypeType?.message ?? types.length === 0
                      ? 'There is no system prototype in this landscape. Contact the platform team to enable them.'
                      : ''
                  }
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="domain"
              control={control}
              rules={{ required: 'Domain is required' }}
              render={({ field }) => (
                <WbAutocomplete
                  options={domains}
                  value={domains.find(d => d.value === field.value) || null}
                  onChange={(_, v) => field.onChange(v?.value ?? '')}
                  getOptionLabel={opt => opt?.label || ''}
                  label="Domain *"
                  placeholder="Select a domain"
                  fullWidth
                  error={!!errors.domain}
                  helperText={errors.domain?.message ?? ''}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="purpose"
              control={control}
              render={({ field }) => (
                <WbTextField
                  {...field}
                  label="Purpose"
                  placeholder="Explain the purpose of the system prototype"
                  fullWidth
                  error={!!errors.purpose}
                  helperText={errors.purpose?.message ?? ''}
                  disabled={isSubmitting}
                  multiline
                  rows={4}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <WbTextField
                  {...field}
                  label="Description"
                  placeholder="Provide a brief description of the system prototype's purpose and scope"
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message ?? ''}
                  disabled={isSubmitting}
                  multiline
                  rows={4}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="status"
              control={control}
              rules={{ required: 'Status is required' }}
              render={({ field }) => (
                <StatusSelector
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.status}
                  helperText={errors.status?.message ?? ''}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default DataProductRequestForm;
